import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { HtmlMailBodyEditor, type HtmlMailBodyInsertHandle } from "@/components/ui/HtmlMailBodyEditor";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import type { AnfrageMailReviewItem, AnfrageMailReviewItemResolved } from "@/lib/anfragen";
import {
  encodeLocalMailAttachments,
  formatAttachmentSize,
  localMailAttachmentsFromFileList,
  type LocalMailAttachment,
  type MailAttachmentPayload,
} from "@/lib/mailAttachments";
import { mailSharepointSuggestions, type SharePointMailItem } from "@/lib/orders";
import { setGlobalUploadSuppressed } from "@/features/globalUpload/globalUploadSuppress";
import { SharePointMailAttachmentList } from "@/components/SharePointMailAttachmentList";
import { anfrageSendLabel, mailHtmlToPlainParts } from "@/pages/anfragen/anfrageWizardUtils";

export type TaMailReviewResult =
  | { action: "cancel"; items: [] }
  | {
      action: "mailto" | "graph";
      items: AnfrageMailReviewItemResolved[];
      sharepointItemIds: string[];
      attachments: MailAttachmentPayload[];
    };

export function TaMailReviewDialog({
  open,
  items,
  auftragIds = [],
  isEntra,
  onResolve,
}: {
  open: boolean;
  items: AnfrageMailReviewItem[];
  auftragIds?: number[];
  isEntra: boolean;
  onResolve: (result: TaMailReviewResult) => void;
}) {
  const [draftItems, setDraftItems] = useState<AnfrageMailReviewItem[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const multi = draftItems.length > 1;
  const active = draftItems[activeIdx] ?? null;

  const [spItems, setSpItems] = useState<SharePointMailItem[]>([]);
  const [spHint, setSpHint] = useState("");
  const [spLoading, setSpLoading] = useState(false);
  const [selectedSp, setSelectedSp] = useState<Set<string>>(new Set());
  const [localAttachments, setLocalAttachments] = useState<LocalMailAttachment[]>([]);
  const [localDropActive, setLocalDropActive] = useState(false);
  const [resolving, setResolving] = useState(false);
  const localFileInputRef = useRef<HTMLInputElement | null>(null);
  const localDropCounterRef = useRef(0);
  const editorHandleRef = useRef<HtmlMailBodyInsertHandle | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraftItems(items.map((it) => ({ ...it })));
    setActiveIdx(0);
    setSelectedSp(new Set());
    setLocalAttachments([]);
    setLocalDropActive(false);
    localDropCounterRef.current = 0;
  }, [open, items]);

  useEffect(() => {
    if (!open) {
      setGlobalUploadSuppressed(false);
      return;
    }
    setGlobalUploadSuppressed(true);
    return () => setGlobalUploadSuppressed(false);
  }, [open]);

  const loadSharepointItems = useCallback(async () => {
    if (!auftragIds.length) {
      setSpItems([]);
      setSpHint("");
      return;
    }
    setSpLoading(true);
    setSpHint("Lade Projektdateien …");
    try {
      const payload = await mailSharepointSuggestions({ auftragIds });
      const list = Array.isArray(payload?.items) ? payload.items : [];
      setSpItems(list);
      const nProj = payload?.sharepoint_projects ?? 0;
      if (!nProj) setSpHint("Keine SharePoint-Projektordner zu diesen Aufträgen gefunden.");
      else if (!list.length)
        setSpHint("In den verknüpften Projektordnern wurden keine Dateien gefunden.");
      else
        setSpHint(
          `${list.length} Datei(en) aus ${nProj} Projektordner(n): auswählen für Direktversand (Microsoft).`,
        );
    } catch {
      setSpHint("Dateivorschläge konnten nicht geladen werden.");
    } finally {
      setSpLoading(false);
    }
  }, [auftragIds]);

  useEffect(() => {
    if (!open) {
      setSpItems([]);
      setSpHint("");
      setSpLoading(false);
      return;
    }
    void loadSharepointItems();
  }, [open, loadSharepointItems]);

  function updateItem(index: number, patch: Partial<AnfrageMailReviewItem>) {
    setDraftItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }

  function persistActiveEditor() {
    const html = editorHandleRef.current?.getHtml();
    if (html == null || !active) return;
    updateItem(activeIdx, { bodyHtml: html });
  }

  function switchActiveIdx(nextIdx: number) {
    persistActiveEditor();
    setActiveIdx(nextIdx);
  }

  function readActiveBodyHtml(): string {
    return editorHandleRef.current?.getHtml() || active?.bodyHtml || "";
  }

  function resolvedItems(): AnfrageMailReviewItemResolved[] {
    const activeHtml = readActiveBodyHtml();
    return draftItems.map((it, idx) => {
      const html = idx === activeIdx ? activeHtml : it.bodyHtml;
      const parts = mailHtmlToPlainParts(html);
      return {
        ...it,
        bodyHtml: html,
        bodyPlain: parts.messagePlain,
        bodyPlainMailto: parts.fullPlain,
      };
    });
  }

  function spIds(): string[] {
    return [...selectedSp];
  }

  function addLocalFiles(fileList: FileList | File[]) {
    const next = localMailAttachmentsFromFileList(fileList);
    if (!next.length) return;
    setLocalAttachments((prev) => [...prev, ...next]);
  }

  async function resolveWithAction(action: "mailto" | "graph") {
    setResolving(true);
    try {
      const attachments = await encodeLocalMailAttachments(localAttachments);
      onResolve({
        action,
        items: resolvedItems(),
        sharepointItemIds: spIds(),
        attachments,
      });
    } finally {
      setResolving(false);
    }
  }

  const mailReady = draftItems.every((it, idx) => {
    const html = idx === activeIdx ? readActiveBodyHtml() : it.bodyHtml;
    const message = mailHtmlToPlainParts(html).messagePlain.trim();
    return it.to.trim().includes("@") && it.subject.trim().length > 0 && message.length > 0;
  });
  const sendLabel = anfrageSendLabel(draftItems.length);

  return (
    <Dialog
      open={open}
      onClose={() => onResolve({ action: "cancel", items: [] })}
      title="E-Mail-Vorschau"
      size="lg"
      zIndexClass="z-[120]"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={() => onResolve({ action: "cancel", items: [] })}>
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!mailReady || multi || resolving}
            title={multi ? "Bei mehreren Lieferanten bitte Direktversand nutzen." : undefined}
            onClick={() => void resolveWithAction("mailto")}
          >
            Im Mail-Programm öffnen
          </Button>
          <Button
            type="button"
            disabled={!mailReady || resolving}
            loading={resolving}
            onClick={() => void resolveWithAction("graph")}
          >
            {sendLabel}
          </Button>
        </>
      }
    >
      {isEntra ? (
        <p className="mb-3 text-xs text-label-secondary">
          Mit Microsoft angemeldet: Direktversand erfolgt über Ihr eigenes Postfach als HTML-Mail. Anhänge werden mitgesendet.
        </p>
      ) : null}

      {multi ? (
        <section className="mb-4 rounded-ios border-2 border-accent/40 bg-accent/5 p-3">
          <p className="mb-2 text-sm font-semibold text-label">
            {draftItems.length} Lieferanten — jede Mail einzeln prüfen
          </p>
          <SegmentedControl
            value={String(activeIdx)}
            onChange={(v) => switchActiveIdx(Number(v))}
            options={draftItems.map((it, idx) => ({
              value: String(idx),
              label: it.supplier_label?.trim() || `Lieferant ${idx + 1}`,
            }))}
            className="mb-3 w-full flex-wrap"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={activeIdx <= 0}
              onClick={() => switchActiveIdx(Math.max(0, activeIdx - 1))}
            >
              ← Vorherige
            </Button>
            <span className="text-center text-sm font-medium text-label">
              Mail {activeIdx + 1} von {draftItems.length}
              {active?.supplier_label ? ` · ${active.supplier_label}` : ""}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={activeIdx >= draftItems.length - 1}
              onClick={() => switchActiveIdx(Math.min(draftItems.length - 1, activeIdx + 1))}
            >
              Nächste →
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {draftItems.map((it, idx) => {
              const message = mailHtmlToPlainParts(
                idx === activeIdx ? readActiveBodyHtml() : it.bodyHtml,
              ).messagePlain.trim();
              const ok = !!(it.to.trim().includes("@") && it.subject.trim() && message);
              return (
                <button
                  key={it.anfrage_id}
                  type="button"
                  onClick={() => switchActiveIdx(idx)}
                  className={clsx(
                    "rounded-full px-2.5 py-1 text-xs font-medium transition",
                    idx === activeIdx
                      ? "bg-accent text-white shadow-sm"
                      : ok
                        ? "bg-fill-tertiary text-label hover:bg-fill-secondary"
                        : "border border-status-orange/40 bg-status-orange/10 text-status-orange",
                  )}
                >
                  {it.supplier_label || `#${idx + 1}`}
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      {active ? (
        <>
          <TextInput
            label="An"
            className="mb-2"
            value={active.to}
            onChange={(e) => updateItem(activeIdx, { to: e.target.value })}
            hint={active.to.trim().includes("@") ? undefined : "Für den Versand ist eine Empfänger-Adresse erforderlich."}
          />
          <TextInput
            label="Betreff"
            className="mb-2"
            value={active.subject}
            onChange={(e) => updateItem(activeIdx, { subject: e.target.value })}
          />
          <label className="mb-1 block text-xs font-medium text-label-secondary">Nachricht</label>
          <p className="mb-2 text-xs text-label-tertiary">
            Text, Betreff und Empfänger kannst du hier noch ändern. Die Signatur wird wie bei Bestellungen angehängt.
          </p>
          <HtmlMailBodyEditor
            key={`${active.anfrage_id}-${activeIdx}`}
            ref={editorHandleRef}
            className="mb-3"
            value={active.bodyHtml}
            minRows={14}
            aria-label="Nachricht"
            onChange={(html) => updateItem(activeIdx, { bodyHtml: html })}
          />
        </>
      ) : (
        <p className="text-sm text-label-secondary">Keine Mail-Daten.</p>
      )}

      {auftragIds.length > 0 ? (
        <section className="rounded-ios border border-separator p-3">
          <div className="mb-1 flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-label">Anhänge aus Projektordner (SharePoint)</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Anhänge neu laden"
              aria-label="Anhänge neu laden"
              loading={spLoading}
              disabled={spLoading}
              onClick={() => void loadSharepointItems()}
            >
              ↻
            </Button>
          </div>
          <p className="mb-2 text-xs text-label-tertiary">{spHint}</p>
          <SharePointMailAttachmentList
            items={spItems}
            selectedIds={selectedSp}
            onToggle={(id, checked) => {
              setSelectedSp((prev) => {
                const next = new Set(prev);
                if (checked) next.add(id);
                else next.delete(id);
                return next;
              });
            }}
          />
        </section>
      ) : null}

      <section className="mt-3 rounded-ios border border-separator p-3">
        <p className="mb-1 text-sm font-semibold text-label">Eigene Anhänge (vom Computer)</p>
        <p className="mb-2 text-xs text-label-tertiary">
          Dateien hier ablegen oder auswählen — werden beim Direktversand (Microsoft) mitgesendet.
        </p>
        <div
          className={clsx(
            "mb-2 rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm transition-colors",
            localDropActive
              ? "border-accent bg-accent/10 text-label"
              : "border-separator text-label-secondary",
          )}
          onDragEnter={(e) => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            e.stopPropagation();
            localDropCounterRef.current += 1;
            setLocalDropActive(true);
          }}
          onDragOver={(e) => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "copy";
          }}
          onDragLeave={(e) => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            e.stopPropagation();
            localDropCounterRef.current = Math.max(0, localDropCounterRef.current - 1);
            if (localDropCounterRef.current === 0) setLocalDropActive(false);
          }}
          onDrop={(e) => {
            if (!e.dataTransfer?.files?.length) return;
            e.preventDefault();
            e.stopPropagation();
            localDropCounterRef.current = 0;
            setLocalDropActive(false);
            addLocalFiles(e.dataTransfer.files);
          }}
        >
          Dateien hierher ziehen
        </div>
        <input
          ref={localFileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) addLocalFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => localFileInputRef.current?.click()}
        >
          Dateien auswählen …
        </Button>
        {localAttachments.length ? (
          <ul className="mt-3 space-y-1 text-sm">
            {localAttachments.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center justify-between gap-2 rounded-md bg-fill-tertiary px-2 py-1"
              >
                <span className="min-w-0 break-all">
                  {entry.file.name}
                  {entry.file.size ? ` · ${formatAttachmentSize(entry.file.size)}` : ""}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`${entry.file.name} entfernen`}
                  onClick={() =>
                    setLocalAttachments((prev) => prev.filter((x) => x.id !== entry.id))
                  }
                >
                  ×
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </Dialog>
  );
}
